import React, { useState, useEffect, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { ShieldCheck, KeyRound, Smartphone, CheckCircle2, AlertTriangle, Copy } from 'lucide-react';

// PUBLIC SuperAdmin invitation activation — no session required. Token comes from the emailed link.
// Flow: validate token → set password → scan authenticator QR + verify → recovery codes → done.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function post(path, body) {
  return fetch(`${BACKEND_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).then(async (res) => {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const detail = data && data.detail;
      const msg = typeof detail === 'string' ? detail
        : (detail && detail.errors ? detail.errors.join(' ') : `Request failed (${res.status})`);
      throw new Error(msg);
    }
    return data;
  });
}

const PW_RULES = [
  { test: (p) => p.length >= 12, label: 'At least 12 characters' },
  { test: (p) => /[a-z]/.test(p), label: 'A lowercase letter' },
  { test: (p) => /[A-Z]/.test(p), label: 'An uppercase letter' },
  { test: (p) => /[0-9]/.test(p), label: 'A number' },
  { test: (p) => /[^A-Za-z0-9]/.test(p), label: 'A symbol' },
];

function getToken() {
  try { return new URLSearchParams(window.location.search).get('token') || ''; }
  catch { return ''; }
}

const Card = ({ children }) => (
  <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
    <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8">{children}</div>
  </div>
);

const SuperAdminActivate = () => {
  const [token] = useState(getToken);
  const [step, setStep] = useState('validating'); // validating | invalid | password | mfa | recovery | done
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');

  const [ticket, setTicket] = useState('');
  const [enroll, setEnroll] = useState(null); // { otpauth_uri, manual_key }
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState([]);

  useEffect(() => {
    if (!token) { setStep('invalid'); setError('This activation link is missing its token.'); return; }
    fetch(`${BACKEND_URL}/api/v1/superadmin/invitation/validate?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error((data && data.detail) || 'This invitation link is invalid or has expired.');
        return data;
      })
      .then((data) => { setInvite(data); setStep('password'); })
      .catch((e) => { setError(e.message); setStep('invalid'); });
  }, [token]);

  const pwOk = PW_RULES.every((r) => r.test(pw)) && pw === pw2;

  const submitPassword = useCallback(async () => {
    setError(''); setBusy(true);
    try {
      const data = await post('/api/v1/superadmin/invitation/activate', { token, password: pw });
      setTicket(data.ticket);
      const enr = await post('/api/v1/superadmin/invitation/mfa/start', { ticket: data.ticket });
      if (enr.already_enrolled && enr.activated) { setStep('done'); return; }
      setEnroll(enr);
      setStep('mfa');
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }, [token, pw]);

  const submitCode = useCallback(async () => {
    setError(''); setBusy(true);
    try {
      const data = await post('/api/v1/superadmin/invitation/mfa/verify', { ticket, code: code.trim() });
      setRecovery(data.recovery_codes || []);
      setStep('recovery');
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }, [ticket, code]);

  const copyKey = (val) => { try { navigator.clipboard.writeText(val); } catch { /* no-op */ } };

  if (step === 'validating') {
    return <Card><div className="text-center text-slate-500 py-8">Checking your invitation…</div></Card>;
  }

  if (step === 'invalid') {
    return (
      <Card>
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-900">Invitation unavailable</h1>
          <p className="text-slate-600 mt-2 text-sm">{error || 'This invitation link is invalid or has expired.'}</p>
          <p className="text-slate-500 mt-4 text-xs">Ask a Servisfy platform owner to send you a new invitation.</p>
        </div>
      </Card>
    );
  }

  if (step === 'done') {
    return (
      <Card>
        <div className="text-center">
          <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-900">Account activated</h1>
          <p className="text-slate-600 mt-2 text-sm">You can now sign in to the Servisfy platform console.</p>
          <a href="/superadmin-portal/login" className="inline-block mt-5 bg-slate-900 text-white rounded-lg px-5 py-2 text-sm font-medium hover:bg-slate-800">
            Go to sign in
          </a>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="text-center mb-6">
        <ShieldCheck className="h-10 w-10 text-slate-800 mx-auto mb-2" />
        <h1 className="text-xl font-bold text-slate-900">Activate your administrator account</h1>
        {invite && <p className="text-slate-500 text-sm mt-1">{invite.email} · {invite.role === 'root_admin' ? 'Root Administrator' : 'Super Administrator'}</p>}
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">{error}</div>
      )}

      {step === 'password' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-slate-700 text-sm font-medium"><KeyRound className="h-4 w-4" /> Step 1 · Set a password</div>
          <input type="password" autoComplete="new-password" className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} />
          <input type="password" autoComplete="new-password" className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Confirm password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
          <ul className="text-xs space-y-1 mt-1">
            {PW_RULES.map((r) => (
              <li key={r.label} className={r.test(pw) ? 'text-green-600' : 'text-slate-400'}>
                {r.test(pw) ? '✓' : '○'} {r.label}
              </li>
            ))}
            <li className={pw && pw === pw2 ? 'text-green-600' : 'text-slate-400'}>{pw && pw === pw2 ? '✓' : '○'} Passwords match</li>
          </ul>
          <button disabled={!pwOk || busy} onClick={submitPassword}
            className="w-full bg-slate-900 text-white rounded-lg py-2 text-sm font-medium disabled:opacity-40 hover:bg-slate-800">
            {busy ? 'Please wait…' : 'Continue to MFA setup'}
          </button>
        </div>
      )}

      {step === 'mfa' && enroll && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-slate-700 text-sm font-medium"><Smartphone className="h-4 w-4" /> Step 2 · Enrol an authenticator (required)</div>
          <p className="text-xs text-slate-500">Scan this QR code with Google Authenticator, Authy, 1Password or any TOTP app, then enter the 6-digit code.</p>
          <div className="flex justify-center py-2">
            <div className="p-3 bg-white border rounded-xl">
              <QRCodeSVG value={enroll.otpauth_uri} size={188} level="M" includeMargin />
            </div>
          </div>
          <div className="text-center">
            <button onClick={() => copyKey(enroll.manual_key)} className="text-xs text-slate-500 inline-flex items-center gap-1 hover:text-slate-700">
              <Copy className="h-3 w-3" /> Can't scan? Copy setup key
            </button>
          </div>
          <input inputMode="numeric" maxLength={8} className="w-full border rounded-lg px-3 py-2 text-center tracking-widest text-lg" placeholder="000000" value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))} />
          <button disabled={code.trim().length < 6 || busy} onClick={submitCode}
            className="w-full bg-slate-900 text-white rounded-lg py-2 text-sm font-medium disabled:opacity-40 hover:bg-slate-800">
            {busy ? 'Verifying…' : 'Verify & activate'}
          </button>
        </div>
      )}

      {step === 'recovery' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-green-700 text-sm font-medium"><CheckCircle2 className="h-4 w-4" /> Save your recovery codes</div>
          <p className="text-xs text-slate-500">Store these somewhere safe. Each code can be used once if you lose your authenticator. They are shown only now.</p>
          <div className="grid grid-cols-2 gap-2 bg-slate-50 border rounded-lg p-3 font-mono text-xs">
            {recovery.map((c) => <div key={c} className="text-slate-800">{c}</div>)}
          </div>
          <button onClick={() => copyKey(recovery.join('\n'))} className="text-xs text-slate-500 inline-flex items-center gap-1 hover:text-slate-700">
            <Copy className="h-3 w-3" /> Copy all codes
          </button>
          <button onClick={() => setStep('done')} className="w-full bg-slate-900 text-white rounded-lg py-2 text-sm font-medium hover:bg-slate-800">
            I've saved them — finish
          </button>
        </div>
      )}
    </Card>
  );
};

export default SuperAdminActivate;
